import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import Autocomplete, {
  createFilterOptions,
} from "@mui/material/Autocomplete";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { auth, db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";
import { countries } from "../constants/countries";

const filterOptions = createFilterOptions({
  matchFrom: "any",
  stringify: (option) => `${option.label} ${option.keywords || ""}`,
  trim: true,
});

// ISOコード(2文字)から国旗絵文字を生成
const getCountryFlag = (keywords) => {
  if (!keywords) return "";
  const match = keywords.match(/\b([A-Z]{2})\b/);
  if (!match) return "";
  const code = match[1];
  return String.fromCodePoint(
    ...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
};

export default function RequiredCountryDialog() {
  const contextTheme = useContext(AppThemeContext);

  const theme = contextTheme || {
    surfaceBg: "#FFFFFF",
    bottomBorder: "rgba(0,0,0,0.08)",
    railActive: "#5A35FF",
    railText: "rgba(60,40,120,0.85)",
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [country, setCountry] = useState("");
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const selectedCountry = useMemo(() => {
    return countries.find((c) => c.label === country) || null;
  }, [country]);

  useEffect(() => {
    if (!auth.currentUser) return;

    const userDocRef = doc(db, "users", auth.currentUser.uid);

    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (!docSnap.exists()) return;

      const data = docSnap.data() || {};
      const prefecture = data.prefecture || "";
      const currentCountry = data.country || "";

      setCountry(currentCountry);

      const hasBasicInfo =
        String(data.registrationName || "").trim() &&
        String(data.registrationFurigana || "").trim() &&
        String(data.registrationEmail || "").trim() &&
        String(data.registrationPhone || "").trim();

      const shouldOpen =
        prefecture === "海外（その他）" &&
        !String(currentCountry).trim() &&
        hasBasicInfo;

      setOpenDialog(Boolean(shouldOpen));
    });

    return () => unsubscribe();
  }, []);

  const handleSave = async () => {
    if (!country.trim()) {
      setError("国名を選択してください");
      return;
    }

    if (!auth.currentUser) return;

    try {
      const userDocRef = doc(db, "users", auth.currentUser.uid);

      await updateDoc(userDocRef, {
        country: country.trim(),
      });

      setError("");
      setOpenDialog(false);
      setSnackbarOpen(true);
    } catch (err) {
      console.error("country update error:", err);
      setError("保存に失敗しました。時間をおいてもう一度お試しください。");
    }
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            px: { xs: 1, sm: 2 },
            py: 2,
            bgcolor: theme.surfaceBg,
            border: `1px solid ${theme.bottomBorder}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: theme.railActive,
            fontWeight: 700,
            textAlign: "center",
            fontSize: { xs: 22, sm: 26 },
            pb: 1,
          }}
        >
          国名のご登録をお願いします
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ maxWidth: 720, mx: "auto", width: "100%" }}>
            <Typography
              sx={{
                color: theme.railText,
                textAlign: "center",
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: 14, sm: 15 },
              }}
            >
              居住地が「海外（その他）」となっているため、
              <br />
              ご利用の前に国名の登録をお願いします。
            </Typography>

            <Autocomplete
              fullWidth
              options={countries}
              value={selectedCountry}
              onChange={(_, newValue) => {
                setCountry(newValue ? newValue.label : "");
                setError("");
              }}
              groupBy={(option) => option.region}
              getOptionLabel={(option) =>
                `${getCountryFlag(option.keywords)} ${option.label}`
              }
              isOptionEqualToValue={(option, value) =>
                option.label === value.label
              }
              filterOptions={filterOptions}
              autoHighlight
              clearOnBlur={false}
              renderOption={(props, option) => (
                <li {...props}>
                  <span style={{ marginRight: 8, fontSize: 20 }}>
                    {getCountryFlag(option.keywords)}
                  </span>
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="国名"
                  placeholder="国名を入力して検索"
                  error={Boolean(error)}
                  helperText={error}
                />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <Typography
                    sx={{
                      px: 2,
                      py: 1,
                      fontSize: 12,
                      fontWeight: 700,
                      color: theme.railActive,
                      bgcolor: "rgba(90,53,255,0.06)",
                      borderTop: `1px solid ${theme.bottomBorder}`,
                    }}
                  >
                    {params.group}
                  </Typography>
                  <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                </li>
              )}
            />

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1.5,
                color: "rgba(0,0,0,0.56)",
                lineHeight: 1.7,
              }}
            >
              ※国名リストは、現在の国連加盟国を基準に表示しています。
              一部の地域・国家については表示されない場合があります。
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pt: 1, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              minWidth: 180,
              height: 48,
              borderRadius: 999,
              fontWeight: 700,
              bgcolor: theme.railActive,
              "&:hover": {
                bgcolor: theme.railActive,
                opacity: 0.92,
              },
            }}
          >
            保存する
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          登録が完了しました。
        </Alert>
      </Snackbar>
    </>
  );
}